import { setItem } from "~/es6/localstorage";
import { node } from "~/util/dom";
import FilterSeasonChart from "./filterseasonchart";
import { _ } from "~/i18n";
import { standardDistanceFormat, standardTimeFormat } from "~/util/format";
import type ActivitiesList from "./list";
import { MONTHS_ABBREVIATIONS } from "./constants";
import { TActivityType } from "~/interfaces";
import ActivitySelect from "~/ui/activities/select";

function formatDate(date) {
	if (date) {
		return [date.getDate(), date.getMonth() + 1, date.getFullYear()].join(". ");
	}

	return "";
}

export default class Filter {
	_owner: ActivitiesList;

	_dom: {
		container: HTMLDivElement;
		types: HTMLDivElement;
		seasons: HTMLDivElement;
		infoValue: HTMLSpanElement;
		show: HTMLSpanElement;
		seasonsList: HTMLDivElement;
		time: HTMLSpanElement;
		count: HTMLSpanElement;
		distance: HTMLSpanElement;
		timeUnit: HTMLSpanElement;
		distanceUnit: HTMLSpanElement;
	};

	_filterCount: number;

	_preventAnimation: boolean;

	_dateFrom: Date;

	_dateTo: Date;

	_loadItems: number;

	_seasonVisible: boolean;

	_empty: boolean;

	_chart: FilterSeasonChart;

	_perPage: number;

	_totalCount: number;

	_infoDate: string;

	_trackType: "all" | TActivityType;

	_season: string;

	_chartsData: Array<{
		items: Array<any>;
		text: any;
		id: any;
		from?: any;
		to?: any;
	}>;

	_availableTypes: Array<TActivityType>;

	_activitySelect: ActivitySelect;

	constructor(owner: ActivitiesList) {
		this._owner = owner;
		this._dom = {
			container: node("div", { className: "filter" }),
			types: node("div", { className: "types" }),
			seasons: node("div", { className: "seasons" }),
			infoValue: node("span", { className: "value", innerHTML: "" }),
			show: node("span", { className: "show" }),
			seasonsList: node("div", { className: "list" }),
			time: node("span", { className: "time" }),
			count: node("span", { className: "count" }),
			distance: node("span", { className: "distance" }),
			timeUnit: node("span", { className: "time", innerHTML: _("mymap.activity.filter.hours") }),
			distanceUnit: node("span", { className: "distance" }),
		};

		this._dateFrom = null;
		this._dateTo = null;
		this._season = null;
		this._trackType = "all";
		this._chartsData = null;
		this._availableTypes = [];
		this._seasonVisible = false;
		this._chart = new FilterSeasonChart(this._owner);
		this._empty = false;
		this._filterCount = 0;
		this._perPage = 20;
		this._loadItems = 0;
		this._totalCount = 0;
		this._preventAnimation = false;
		this._infoDate = "";
		this._activitySelect = null;

		this._create();
		this.season = "all";
		this.clear();

		this.loadSummary();
	}

	get container() {
		return this._dom.container;
	}

	set filter({ dateFrom, dateTo, type, loadItems }: {
		dateFrom?: Date;
		dateTo?: Date;
		type?: "all" | TActivityType;
		loadItems?: number;
	}) {
		if (dateFrom) {
			this._dateFrom = new Date(dateFrom);
		}

		if (dateTo) {
			this._dateTo = new Date(dateTo);
		}

		if (type && type !== this._trackType) {
			this._setType(type);
		}

		if (loadItems) {
			this._loadItems = loadItems;
		}
	}

	get filter() {
		return {
			dateFrom: this._dateFrom,
			dateTo: this._dateTo,
			type: this._trackType,
			loadItems: this._loadItems,
		};
	}

	async getItems() {
		if (this._filterCount > 0 && this._owner.page > 0 && this._filterCount - (this._perPage * this._owner.page) < 0) {
			return new Promise((resolve, reject) => {
				reject();
			});
		}

		if (this._chartsData === null) {
			await this.loadSummary();
		}

		const filter = {
			/* eslint-disable camelcase */
			per_page: this._loadItems ? this._loadItems : this._perPage,
			page: this._owner.page * this._perPage,
			...this._trackType === "all" ? {} : { track_type: [this._trackType] },
			...this._dateFrom === null ? {} : { date_from: this._dateFrom.getTime() / 1000 },
			...this._dateTo === null ? {} : { date_to: this._dateTo.getTime() / 1000 },
			/* eslint-enable camelcase */
		};

		const data = await Mapy.ProviderMapyBox.trackList(filter);

		const activities = data.activities;
		const items = activities.items || [];
		let minDate = new Date();
		let maxDate = new Date();
		const now = new Date();

		if (this._dateFrom && this._dateTo) {
			minDate = this._dateFrom;
			maxDate = this._dateTo;
		} else if ("years" in activities.summary) {
			const years = Object.keys(activities.summary.years).sort((firstItem, secondItem) => parseInt(firstItem) < parseInt(secondItem) ? -1 : 1);
			const minY = years[0];
			const maxY = years[years.length - 1];

			if (minY && maxY) {
				minDate = new Date(parseInt(minY), 0, 1, 0, 0, 0);
				//maxDate = new Date(parseInt(maxY), 12, -1, 23, 59, 59);
			}
		}

		this._setInfoDate(minDate, maxDate.getTime() > now.getTime() ? now : maxDate);
		this._setSummary(activities.summary);
		this._preventAnimation = false;
		this._filterCount = "total_count" in activities.summary && parseInt(activities.summary.total_count.toString()) ? parseInt(activities.summary.total_count.toString()) : 0;

		this._loadItems = 0;

		return items;
	}

	get totalCount() {
		return this._totalCount;
	}

	set preventAnimation(prevent) {
		this._preventAnimation = prevent;
	}

	set season(season) {
		this._season = season;

		// vezmeme predchozi co bylo vybrano, odebereme a nastavime nove
		let selected = this._dom.seasonsList.querySelector(".active");

		if (selected) {
			selected.classList.remove("active");
		}

		selected = this._dom.seasonsList.querySelector(`a[data-season='${season}']`);

		if (selected) {
			selected.classList.add("active");
		}
	}

	get infoDate() {
		return this._infoDate;
	}

	clear() {
		this._showChart(false);

		this._dateFrom = null;
		this._dateTo = null;
		this._season = "all";
		this._trackType = "all";
		this._chartsData = null;
		this._availableTypes = [];
		this._createTypes();
		this._chart.clear();
		this._infoDate = "";
	}

	async loadSummary() {
		const params = {
			/* eslint-disable camelcase */
			...this._trackType === "all" ? {} : { track_type: [this._trackType] },
			/* eslint-enable camelcase */
		};

		this._empty = false;

		let data = await Mapy.ProviderMapyBox.trackSummary(params);

		// pokud dostaneme zpet info, ze mame jen jeden typ aktivity, ale na ten zrovna nekoukame, zmenime, na co koukame a znovu nacteme
		if (this._trackType !== "all") {
			// ma jen jeden typ aktivity a na ten by nekoukal, takze by nevidel nic - zmenime na ten jeden, co ma
			if (data.summary.activities.length === 1 && this._trackType !== data.summary.activities[0]) {
				this._setType(this._availableTypes[0]);
				data = await Mapy.ProviderMapyBox.trackSummary({
					/* eslint-disable camelcase */
					track_type: [this._trackType],
					/* eslint-enable camelcase */
				});
			// ma vic typu aktivity, ale ten, na ktery chce koukat, uz mezi nimi neni - asi tedy smazal nebo zmenil posledni toho typu a filtr zmenime na all
			} else if (data.summary.activities.indexOf(this._trackType) === -1) {
				this._setType("all");
				data = await Mapy.ProviderMapyBox.trackSummary({});
				this._loadItems = 0;
			}
		}

		if (("activities" in data.summary && this._trackType === "all") || this._trackType !== "all") {
			this._empty = !data.summary.total_count && this._trackType === "all";
			this._totalCount = data.summary.total_count;
			this._prepareSummaryData(data.summary);
			this._availableTypes = data.summary.activities;
			this._setSeasonList(this._chartsData);
			this._createTypes();
			if (this._availableTypes.length === 1) {
				this._setType(this._availableTypes[0]);
			} else {
				this._setType(this._trackType);
			}
			this._chart.data = this._chartsData;
			this._chart.redraw();
		} else {
			this._empty = true;
			this._totalCount = 0;
		}

		this._dom.container.classList[this._empty ? "add" : "remove"]("empty");
	}

	handleEvent(event) {
		if (event.type === "click") {
			const season = event.target.getAttribute("data-season");

			if (season && !this._empty) {
				this._season = season;
				this._chart.show = season;
				this._chart.filterItems();

				Mapy.getComponent("logger").log("casovy_filtr_interakce", {
					setting: "obdobi",
				});
			} else if ((event.target === this._dom.show || event.target === this._dom.infoValue) && !this._empty) {
				this._showChart();

				Mapy.getComponent("logger").log("casovy_filtr_zobrazeni", {
					visible: this._seasonVisible,
				});
			}
		}
	}

	_create() {
		// typy - mhd, pesky apod.
		this._createTypes();
		this._dom.container.appendChild(this._dom.types);

		// celkove obdobi, rozbaleni vyberu mesicu
		const infoLine = node("p", { className: "info-line" });

		infoLine.appendChild(node("span", { className: "desc", innerHTML: `${_("mymap.activity.filter.yourActivities")}:` }));
		this._dom.infoValue.addEventListener("click", this);
		this._dom.show.addEventListener("click", this);
		infoLine.appendChild(this._dom.infoValue);
		infoLine.appendChild(this._dom.show);
		this._dom.container.appendChild(infoLine);
		this._dom.seasons.appendChild(this._dom.seasonsList);
		this._dom.container.appendChild(this._dom.seasons);

		// vytvoreni vyberu mesicu
		this._dom.seasons.appendChild(this._chart.container);

		// shrnuti vsech tracku
		this._createSummary();
	}

	_createTypes() {
		if (this._activitySelect) {
			this._activitySelect.container.remove();
			this._activitySelect.destroy();
		}

		this._activitySelect = new ActivitySelect({
			selected: this._trackType,
			onChange: async type => {
				if (type && !this._empty) {
					this._showChart(this._seasonVisible);
					this._owner.changeFilter();
					this._owner.getItems(this._dateFrom, this._dateTo, type);
					await this.loadSummary();
					setItem("myMapsActivitiesType", this._trackType);

					Mapy.getComponent("logger").log("casovy_filtr_interakce", {
						setting: "type",
					});
				}
			},
			options: this._availableTypes,
		});
		this._dom.types.append(this._activitySelect.container);
	}

	_createSummary() {
		const summary = node("div", { className: "summary" });
		const summaryTitles = node("div", { className: "titles" });
		const summaryValues = node("div", { className: "values" });
		const summaryUnits = node("div", { className: "units" });
		const timeTitle = node("span", { className: "time", innerHTML: _("mymap.activity.filter.totalTime") });
		const countTitle = node("span", { className: "time", innerHTML: _("mymap.activity.filter.totalCount") });
		const distanceTitle = node("span", { className: "time", innerHTML: _("mymap.activity.filter.totalDistance") });
		const countUnit = node("span", { className: "count" });

		summaryTitles.appendChild(timeTitle);
		summaryTitles.appendChild(countTitle);
		summaryTitles.appendChild(distanceTitle);

		summaryValues.appendChild(this._dom.time);
		summaryValues.appendChild(this._dom.count);
		summaryValues.appendChild(this._dom.distance);

		summaryUnits.appendChild(this._dom.timeUnit);
		summaryUnits.appendChild(countUnit);
		summaryUnits.appendChild(this._dom.distanceUnit);

		summary.appendChild(summaryTitles);
		summary.appendChild(summaryValues);
		summary.appendChild(summaryUnits);

		this._dom.container.appendChild(summary);
	}

	// eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
	_setType(newTransportType) {
		this._trackType = newTransportType;

		if (this._activitySelect) {
			this._activitySelect.selected = newTransportType;
		}
	}

	_setSummary(summary) {
		const seconds = parseFloat(summary.total_time) ? parseFloat(summary.total_time) : 0;
		const count = parseFloat(summary.total_count) ? parseFloat(summary.total_count) : 0;
		const meters = parseFloat(summary.total_length) || 0;

		let secondInt = null;
		let countInt = null;
		let distanceInt = null;
		let secondsActual = 0;
		let countActual = 0;
		let distanceActual = 0;

		const time = standardTimeFormat(seconds, true);

		this._dom.time.innerHTML = time.time;
		this._dom.timeUnit.innerHTML = _(`time.full.${time.key}`);
		this._dom.count.innerHTML = count.toString();

		const distance = standardDistanceFormat(meters.toString());

		this._dom.distance.innerHTML = Math.round(distance.distanceNumber).toString();
		this._dom.distanceUnit.innerHTML = distance.unit;

		if (!this._preventAnimation) {
			if (seconds > 0) {
				secondInt = setInterval((con, unitCon) => {
					secondsActual += Math.floor(seconds / (seconds > 100 ? 100 : seconds));
					const formatTime = standardTimeFormat(secondsActual, true);

					con.innerHTML = formatTime.time;
					unitCon.innerHTML = _(`time.full.${formatTime.key}`);
					if (secondsActual >= seconds) {
						con.innerHTML = time.time;
						unitCon.innerHTML = _(`time.full.${time.key}`);
						clearInterval(secondInt);
					}
				}, 10, this._dom.time, this._dom.timeUnit);
			}

			if (count > 0) {
				countInt = setInterval(container => {
					countActual += Math.floor(count / (count > 100 ? 100 : count));
					container.innerHTML = countActual.toString();
					if (countActual >= count) {
						container.innerHTML = count.toString();
						clearInterval(countInt);
					}
				}, 10, this._dom.count);
			}

			if (distance.distanceNumber > 0) {
				distanceInt = setInterval(container => {
					distanceActual += Math.floor(distance.distanceNumber / (distance.distanceNumber > 100 ? 100 : distance.distanceNumber));
					container.innerHTML = distanceActual.toString();
					if (distanceActual >= distance.distanceNumber) {
						container.innerHTML = Math.floor(distance.distanceNumber).toString();
						clearInterval(distanceInt);
					}
				}, 10, this._dom.distance);
			}
		}
	}

	_setInfoDate(fromDate, toDate) {
		this._dom.infoValue.innerHTML = `${formatDate(fromDate)} &#0150; ${formatDate(toDate)}`;
		this._infoDate = this._dom.infoValue.textContent;
	}

	_setSeasonList(charts) {
		let active = "";
		const activeEl = this._dom.seasonsList.querySelector(".active");

		if (activeEl) {
			active = activeEl.getAttribute("data-season");
		}

		this._dom.seasonsList.innerHTML = "";
		this._dom.seasonsList.classList[charts.length > 7 ? "add" : "remove"]("compact");

		charts.forEach((chart, index) => {
			const item = node("a");

			item.innerHTML = index === 0 ? _("mymap.activity.filter.wholeSeason") : chart.text;
			item.setAttribute("data-season", chart.id);
			item.setAttribute("data-log-ignore", "1");
			item.addEventListener("click", this);

			if (chart.id === active) {
				item.classList.add("active");
			}

			this._dom.seasonsList.appendChild(item);
		});
	}

	_showChart(state?) {
		this._seasonVisible = typeof state === "undefined" ? !this._seasonVisible : state;
		this._dom.show.innerHTML = this._seasonVisible ? "Z" : "R";
		this._dom.seasons.classList[this._seasonVisible ? "remove" : "add"]("hide");
		if (!state && this._chart.data && this._season === "all" && !this._chart.fromToSetted) {
			this._chart.show = "all";
		}
	}

	_prepareSummaryData(data) {
		const years = "years" in data ? Object.keys(data.years).sort((firstItem, secondItem) => parseInt(firstItem) < parseInt(secondItem) ? -1 : 1) : [];

		this._chartsData = [{
			items: [],
			text: "all",
			id: "all",
			from: null,
			to: null,
		}];

		// kontrola, zda jsou roky kompletni, prazdne pripadne doplnime
		for (let index = parseInt(years[0]); index <= new Date().getFullYear(); index++) {
			this._chartsData.push({
				items: [],
				text: index,
				id: index.toString(),
			});
			const chart = this._chartsData[this._chartsData.length - 1];
			let value = 0;

			const months = data.years[index];

			MONTHS_ABBREVIATIONS.forEach((text, key) => {
				const month = key + 1;

				value += months && month in months ? months[month] : 0;
				chart.items.push({
					text: _(`calendar.shortMonths.${text}`),
					id: month.toString(),
					value: months && month in months ? months[month] : 0,
					from: new Date(index, month - 1, 1, 0, 0, 0).getTime(),
					to: new Date(index, month - 1, new Date(index, month, 0).getDate(), 23, 59, 59).getTime(),
				});
			});

			this._chartsData[0].items.push({
				text: index,
				id: index,
				value,
				from: new Date(index, 0, 1, 0, 0, 0).getTime(),
				to: new Date(index, 11, 31, 23, 59, 59).getTime(),
			});
		}
	}
}
